// Autenticação: entrar, segundo fator, sair.
//
// A rotação do refresh e o retry do 401 vivem em cliente.ts. Aqui só ficam as
// chamadas e o estado de "quem está logado".

import { api } from "./cliente";
import { deposito } from "./deposito";
import { guardarVagaDaAba, vagaDaAba } from "./vaga";
import type { Sessao } from "./tipos";

/** Login com 2FA não abre sessão: devolve um desafio de uso único. */
export interface Desafio {
  desafio: string;
  metodos: string[];
}

export type ResultadoLogin =
  | { tipo: "sessao"; sessao: Sessao }
  | { tipo: "segundo_fator"; desafio: Desafio };

function guardar(sessao: Sessao): Sessao {
  if (sessao.token) deposito.guardarToken(sessao.token);
  // A VAGA É DESTA ABA. Entrar com outra conta nesta aba troca a vaga dela e
  // não encosta nas outras — é o que permite duas contas em duas abas.
  if (sessao.vaga !== undefined && sessao.vaga !== null) {
    guardarVagaDaAba(sessao.vaga);
  }
  // O campo `refresh` do corpo é IGNORADO de propósito: ele existe no contrato
  // para integrações e scripts, que não têm como usar cookie. O navegador usa
  // o cookie HttpOnly que a mesma resposta gravou, e guardar uma cópia aqui
  // desfaria a proteção inteira. Ver deposito.ts.
  return sessao;
}

export async function entrar(
  email: string,
  senha: string,
  lembrar = false,
): Promise<ResultadoLogin> {
  // 202 significa "a senha está certa, falta o segundo fator" — não é erro, e
  // por isso esta é a única chamada que olha o status.
  const { status, dados } = await api.postComStatus<Sessao | Desafio>("/auth/login", {
    email,
    senha,
    lembrar,
  });
  if (status === 202) return { tipo: "segundo_fator", desafio: dados as Desafio };
  return { tipo: "sessao", sessao: guardar(dados as Sessao) };
}

export async function verificarSegundoFator(desafio: string, codigo: string): Promise<Sessao> {
  return guardar(await api.post<Sessao>("/auth/2fa/verificar", { desafio, codigo }));
}

export async function sair(): Promise<void> {
  try {
    // Revoga no servidor, para o token não continuar valendo se alguém o tiver
    // copiado. Falhar aqui não pode impedir a saída local.
    await api.post<void>("/auth/logout");
  } finally {
    deposito.limpar();
    // A vaga sai junto: o servidor acabou de apagar o cookie dela, e deixar
    // o número aqui faria a aba renovar numa vaga vazia e cair no login sem
    // nunca tentar as contas que ainda estão abertas.
    guardarVagaDaAba(null);
  }
}

/** As contas que ESTE navegador tem abertas, com a vaga de cada uma. */
export interface ContaAberta {
  vaga: number;
  caixa: string;
}

export async function contasDoNavegador(): Promise<ContaAberta[]> {
  try {
    const r = await api.get<{ itens?: ContaAberta[] }>("/auth/contas");
    return r.itens ?? [];
  } catch {
    // Lista de contas é conveniência. Falhar aqui não pode impedir o login.
    return [];
  }
}

/**
 * Reconstrói a sessão ao abrir a página.
 *
 * O token de acesso vive só em memória, então todo F5 começa sem ele. O cookie
 * HttpOnly sobrevive, e é ele que troca por um token novo — inclusive numa aba
 * recém-aberta, porque cookie é compartilhado entre abas.
 */
export async function retomar(): Promise<Sessao | null> {
  // Não dá para perguntar "existe sessão?" antes de tentar: o cookie é
  // invisível a este código, por construção. Então tenta-se sempre, e a
  // resposta do servidor é quem sabe. O custo é uma requisição a mais no
  // primeiro carregamento de quem não está logado.
  // ABA QUE JÁ SABE A VAGA retoma direto a conta dela. É o caso de um F5.
  if (vagaDaAba() !== null) {
    try {
      return guardar(await api.post<Sessao>(`/auth/u/${vagaDaAba()}/refresh`));
    } catch {
      // A vaga desta aba morreu (saiu em outra aba, ou expirou). Em vez de
      // ir direto ao login, cai na descoberta abaixo: pode haver OUTRA conta
      // aberta neste navegador, e mandar a pessoa digitar senha tendo sessão
      // viva é o contrário do que ela espera.
      guardarVagaDaAba(null);
    }
  }

  // ABA NOVA: não sabe a vaga, e não pode descobrir sozinha — o índice é um
  // cookie HttpOnly. Pergunta ao servidor e retoma a primeira conta aberta.
  // Sem isto, abrir o webmail numa segunda aba cairia no login mesmo havendo
  // sessão, que é um comportamento que as pessoas usam todo dia.
  for (const conta of await contasDoNavegador()) {
    try {
      return guardar(await api.post<Sessao>(`/auth/u/${conta.vaga}/refresh`));
    } catch {
      // Vaga no índice sem cookie válido: segue para a próxima.
    }
  }

  // Último recurso: o caminho antigo, para quem entrou antes das vagas
  // existirem. O servidor migra a sessão e devolve a vaga.
  try {
    return guardar(await api.post<Sessao>("/auth/refresh"));
  } catch {
    deposito.limpar();
    guardarVagaDaAba(null);
    return null;
  }
}

export const temSessao = (): boolean => deposito.token() !== null;

/**
 * Retoma UMA vaga específica — a troca de conta dentro da mesma aba.
 *
 * Separado de `retomar()` de propósito: aquele descobre qual vaga usar,
 * este obedece. Misturar os dois faria a troca de conta cair na descoberta e
 * voltar para a conta anterior quando a pedida falhasse, que é o contrário
 * do que quem clicou pediu.
 */
export async function retomarVaga(vaga: number): Promise<Sessao> {
  return guardar(await api.post<Sessao>(`/auth/u/${vaga}/refresh`));
}
